import React from "react";
import {
  Rocket,
  TrendingUp,
  Zap,
  Sparkles,
  ArrowRight,
  Check,
  RefreshCw,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";
import { Button } from "../ui/button";
import { PACKAGES } from "../../mock";
import { selectPackage } from "../../lib/selectPackage";

const ICONS = { Rocket, TrendingUp, Zap, Sparkles };

// TikTok icon (Lucide doesn't ship a stock TikTok, so we render our own)
const TikTokIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.5a8.16 8.16 0 0 0 4.77 1.52V6.6a4.85 4.85 0 0 1-1.84-.05z" />
  </svg>
);

const PLATFORM_META = {
  Facebook: { Icon: Facebook, bg: "bg-[#1877F2]", label: "Facebook" },
  Instagram: {
    Icon: Instagram,
    bg: "bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5]",
    label: "Instagram",
  },
  TikTok: { Icon: TikTokIcon, bg: "bg-slate-900", label: "TikTok" },
  LinkedIn: { Icon: Linkedin, bg: "bg-[#0A66C2]", label: "LinkedIn" },
};

export default function Packages() {
  return (
    <section
      id="packages"
      className="py-24 lg:py-32 bg-slate-50 relative overflow-hidden"
    >
      <div className="absolute -top-20 left-1/4 h-72 w-72 rounded-full bg-sky-100 blur-3xl opacity-60" />
      <div className="absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-blue-100 blur-3xl opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="section-eyebrow">Packages</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
            Social media packages.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Every business is different, so every package is shaped around
            your goals, audience and budget. All packages cover Facebook,
            Instagram, TikTok and LinkedIn &mdash; you choose the mix.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-sky-200 shadow-sm text-sm text-slate-700">
            <RefreshCw className="h-3.5 w-3.5 text-sky-600" />
            <span>
              <strong className="text-slate-900">4-weekly rolling contract</strong>
              <span className="text-slate-500">
                {" "}
                &middot; Cancel anytime with 2 weeks notice
              </span>
            </span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PACKAGES.map((p) => {
            const Icon = ICONS[p.icon];
            return (
              <div
                key={p.name}
                className={`relative rounded-3xl border p-7 lg:p-8 bg-white card-lift flex flex-col ${
                  p.popular
                    ? "border-sky-300 ring-2 ring-sky-200/60 shadow-lg shadow-sky-100/50"
                    : "border-slate-200"
                }`}
              >
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-sky-500 text-white text-[11px] font-semibold uppercase tracking-wider shadow-sm">
                    Most popular
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div
                    className={`h-11 w-11 rounded-2xl flex items-center justify-center ${
                      p.popular
                        ? "bg-sky-500 text-white"
                        : "bg-slate-900 text-white ring-4 ring-sky-50"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                </div>

                <h3 className="mt-5 text-xl font-semibold text-slate-900">
                  {p.name}
                </h3>
                <p className="text-sm text-sky-700 font-medium mt-1">
                  {p.tagline}
                </p>

                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    {p.suffix}
                  </span>
                  <span className="text-4xl font-semibold text-slate-900">
                    {p.price}
                  </span>
                </div>

                {/* Platforms */}
                <div className="mt-5">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Platforms
                  </div>
                  <div className="flex gap-1.5">
                    {p.platforms?.map((plat) => {
                      const meta = PLATFORM_META[plat];
                      if (!meta) return null;
                      const P = meta.Icon;
                      return (
                        <div
                          key={plat}
                          title={meta.label}
                          aria-label={meta.label}
                          className={`h-8 w-8 rounded-lg ${meta.bg} text-white flex items-center justify-center shadow-sm`}
                        >
                          <P className="h-4 w-4" />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Post count */}
                <div className="mt-5 p-4 rounded-2xl bg-sky-50 border border-sky-100">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-semibold text-sky-700 leading-none">
                      {p.posts}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-slate-600 leading-snug">
                    {p.postsSuffix}
                  </div>
                </div>

                {/* Features */}
                {p.features?.length > 0 && (
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-xs text-slate-700 leading-relaxed"
                      >
                        <Check className="h-3.5 w-3.5 text-sky-600 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-5 pt-4 border-t border-slate-100 text-[11px] text-slate-500 leading-relaxed">
                  Each package is tailored to your specific business needs.
                </div>

                <Button
                  data-testid={`social-package-cta-${p.name.toLowerCase()}`}
                  onClick={() =>
                    selectPackage(
                      "Social media & marketing",
                      `Hi, I'm interested in the ${p.name} social media package (${p.suffix} ${p.price}).`
                    )
                  }
                  className={`mt-4 rounded-full w-full h-11 group ${
                    p.popular
                      ? "bg-sky-600 hover:bg-sky-700 text-white"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                >
                  Get in touch
                  <ArrowRight className="ml-1.5 h-4 w-4 btn-arrow" />
                </Button>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center text-sm text-slate-600">
          Not sure which package fits?{" "}
          <a
            href="/#contact"
            className="text-sky-700 font-medium hover:underline underline-offset-4"
          >
            Get in touch
          </a>{" "}
          and we&rsquo;ll recommend the right starting point for you.
        </div>
      </div>
    </section>
  );
}
